import { signal } from '@preact/signals';
import type { FlowerId } from '../game/flowers';
import type { IntentTone, Tool } from '../game/interaction';
import type { PlayerInfo } from '../net/protocol';

export type PanelId = 'market' | 'orders' | 'almanac' | 'friends' | 'settings' | 'house' | 'help';
export type ToastTone = 'info' | 'good' | 'bad' | 'rare';

export interface Toast {
  id: number;
  text: string;
  tone: ToastTone;
}

export interface ChatLine {
  id: number;
  name: string;
  color: string;
  text: string;
  system: boolean;
  time: number;
}

export interface HoverInfo {
  x: number;
  y: number;
  label: string;
  tone: IntentTone;
}

export type NetStatus =
  | { mode: 'solo' }
  | { mode: 'connecting'; role: 'host' | 'client' }
  | { mode: 'host'; code: string }
  | { mode: 'client'; code: string; hostName: string };

/** Reactive UI state shared between the game loop and Preact components. */
export const ui = {
  started: signal(false),
  panel: signal<PanelId | null>(null),
  tool: signal<Tool>('hand'),
  seed: signal<FlowerId | null>('daisy'),
  hover: signal<HoverInfo | null>(null),
  toasts: signal<Toast[]>([]),
  chat: signal<ChatLine[]>([]),
  chatOpen: signal(false),
  net: signal<NetStatus>({ mode: 'solo' }),
  netError: signal<string | null>(null),
  players: signal<PlayerInfo[]>([]),
  /** Bumped whenever the game state changes; components read it to re-render. */
  version: signal(0),
  levelUp: signal<number | null>(null),
  sleeping: signal(false),
  /** Prefilled room code from a ?join= link. */
  pendingJoin: signal<string | null>(null),
};

let nextId = 1;
const TOAST_LIMIT = 4;

export function toast(text: string, tone: ToastTone = 'info', seconds = 3.6): void {
  const item: Toast = { id: nextId++, text, tone };
  ui.toasts.value = [...ui.toasts.value, item].slice(-TOAST_LIMIT);
  setTimeout(() => {
    ui.toasts.value = ui.toasts.value.filter((t) => t.id !== item.id);
  }, seconds * 1000);
}

const CHAT_LIMIT = 40;

export function pushChat(line: Omit<ChatLine, 'id' | 'time'>): void {
  ui.chat.value = [...ui.chat.value, { ...line, id: nextId++, time: Date.now() }].slice(
    -CHAT_LIMIT,
  );
}

export function openPanel(panel: PanelId | null): void {
  ui.panel.value = panel;
}

export function togglePanel(panel: PanelId): void {
  ui.panel.value = ui.panel.value === panel ? null : panel;
}
